import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Youtube, Linkedin, Mail, Globe, ArrowUpRight, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Footer() {
  const { t } = useLanguage();
  const [partners, setPartners] = useState({ column_title: 'Partneri', links: [] });

  useEffect(() => {
    axios.get(`${API}/api/footer/partners`).then(r => setPartners(r.data)).catch(() => {});
  }, []);

  const catItems = [
    { s: 'news', key: 'footer_cat_news' },
    { s: 'politics', key: 'footer_cat_politics' },
    { s: 'sport', key: 'footer_cat_sports' },
    { s: 'business', key: 'footer_cat_business' },
    { s: 'technology', key: 'footer_cat_technology' },
    { s: 'culture', key: 'footer_cat_culture' },
    { s: 'health', key: 'footer_cat_health' },
  ];

  const linkItems = [
    { to: '/about', key: 'footer_link_about' },
    { to: '/contact', key: 'footer_link_contact' },
    { to: '/privacy', key: 'footer_link_privacy' },
    { to: '/cookies', key: 'footer_link_cookies' },
    { to: '/imprint', key: 'footer_link_imprint' },
    { to: '/advertise', key: 'footer_link_advertise' },
    { to: '/submit-news', key: 'footer_link_submit_news' },
  ];

  return (
    <footer className="bg-navy dark:bg-navy-dark text-white relative" data-testid="footer">
      {/* Top gradient border */}
      <div className="h-px bg-gradient-to-r from-transparent via-sky-accent/40 to-transparent" />

      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">

          {/* Brand Column */}
          <div>
            <Link to="/" className="flex items-center gap-2 mb-4 group">
              <img
                src="https://customer-assets.emergentagent.com/job_d378754e-4674-4e88-b637-20552b388a6b/artifacts/82cxbgvt_CroatiaTimesLogoWebCMS.png"
                alt="Croatia Times"
                className="h-10 w-auto brightness-200"
              />
              <span className="font-display text-base font-bold text-white group-hover:text-sky-accent transition-colors sr-only">Croatia Times</span>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-5" data-testid="footer-description">{t('footer_description')}</p>
            <div className="flex items-center gap-2">
              {[
                { icon: Facebook, label: 'Facebook' },
                { icon: Twitter, label: 'Twitter' },
                { icon: Instagram, label: 'Instagram' },
                { icon: Youtube, label: 'YouTube' },
                { icon: Linkedin, label: 'LinkedIn' },
              ].map(({ icon: Icon, label }) => (
                <a key={label} href="#" className="p-2 bg-white/5 rounded-lg hover:bg-sky-accent hover:text-white transition-all" aria-label={label}>
                  <Icon size={14} />
                </a>
              ))}
            </div>
          </div>

          {/* Partners */}
          <div>
            <h4 className="font-display text-sm font-bold uppercase tracking-wider mb-4 text-white">{partners.column_title}</h4>
            <ul className="space-y-2">
              {partners.links.map((link, i) => (
                <li key={i}>
                  <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-gray-400 text-sm hover:text-sky-accent transition-colors flex items-center gap-1 group" data-testid={`footer-partner-${i}`}>
                    {link.name}
                    <ExternalLink size={10} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display text-sm font-bold uppercase tracking-wider mb-4 text-white">{t('footer_quick_links')}</h4>
            <ul className="space-y-2">
              {linkItems.map(item => (
                <li key={item.to}>
                  <Link to={item.to} className="text-gray-400 text-sm hover:text-sky-accent transition-colors flex items-center gap-1 group">
                    {t(item.key)}
                    <ArrowUpRight size={10} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Stay Informed */}
          <div>
            <h4 className="font-display text-sm font-bold uppercase tracking-wider mb-4 text-white">{t('footer_stay_informed')}</h4>
            <p className="text-gray-400 text-sm mb-3">{t('footer_newsletter_text')}</p>
            <form className="space-y-2" onSubmit={e => e.preventDefault()}>
              <input
                type="email"
                placeholder={t('footer_email_placeholder')}
                className="w-full px-3 py-2 rounded-lg text-xs bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-sky-accent font-display"
              />
              <button type="submit" className="w-full px-4 py-2 bg-sky-accent hover:bg-sky-accent-dark text-white rounded-lg text-xs font-display font-semibold transition-colors">
                {t('footer_subscribe')}
              </button>
            </form>
            <div className="mt-4 pt-4 border-t border-white/5">
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <Mail size={13} />
                <a href="mailto:info@timescroatia.com" className="hover:text-sky-accent transition-colors">info@timescroatia.com</a>
              </div>
              <div className="flex items-center gap-2 text-gray-400 text-sm mt-1">
                <Globe size={13} />
                <a href="https://timescroatia.com" className="hover:text-sky-accent transition-colors">timescroatia.com</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/5">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-2 text-[11px] text-gray-500">
            <span>&copy; {t('footer_copyright')}</span>
            <div className="flex items-center gap-3 text-center flex-wrap justify-center">
              <a href="https://infinitybusiness.org" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:opacity-80 transition-opacity" data-testid="footer-infinitybusiness-link">
                <img src="https://customer-assets.emergentagent.com/job_f780b4e1-eddf-412d-9d52-27b94ba3e14c/artifacts/4ulvrdm4_IBLogo.png" alt="InfinityBusiness" className="h-5 w-auto" />
                <span className="text-gray-400 text-[11px]">{t('footer_developed_by')} <span className="font-semibold text-sky-accent">infinitybusiness.org</span></span>
              </a>
              <span className="text-gray-600">|</span>
              <a href="https://markedhost.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:opacity-80 transition-opacity" data-testid="footer-markedhost-link">
                <span className="text-gray-400 text-[11px]">{t('footer_hosted_by')}</span>
                <img src="https://customer-assets.emergentagent.com/job_f780b4e1-eddf-412d-9d52-27b94ba3e14c/artifacts/0a7hxoip_markedpotpislogo.png" alt="MarkedHost" className="h-5 w-auto" />
              </a>
            </div>
            <span className="text-center">
              {t('footer_ai_notice')}{' '}
              <Link to="/submit-bug" className="text-sky-accent hover:underline">{t('footer_here')}</Link>.
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
